// ###### IMPORTS
import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { toast } from 'react-hot-toast';
import { createAssetReturn } from '../../viewServices/assets_api';
import SpinnerLoad from 'views/finance/moduleComponents/SpinnerLoad';

// ###### HELPERS
const todayYYYYMMDD = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const parseDecimal = (v) => {
  if (v === '' || v == null) return null;
  const n = parseFloat(String(v).replace(',', '.'));
  return Number.isNaN(n) ? null : n;
};

const PAYMENT_TYPES = [
  { value: 'DIVIDENDOS', label: 'Dividendos' },
  { value: 'JURO CAPITAL PROPRIO', label: 'Juro Capital Próprio' },
  { value: 'REND IMOBILIARIO', label: 'Rendimento Imobiliário' },
  { value: 'other', label: 'Other' },
];

const getInitialState = () => ({
  asset: '',
  payment_type: 'DIVIDENDOS',
  payment_date: todayYYYYMMDD(),
  payment_value: '',
  quantity_at_time: '',
  notes: '',
});

// ###### COMPONENT
const ModalReturnAdd = ({ isOpen, onClose, onSuccess, assets = [] }) => {
  const [form, setForm] = useState(getInitialState);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) setForm(getInitialState());
  }, [isOpen]);

  if (!isOpen) return null;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const assetId = form.asset?.trim() ? Number(form.asset) : null;
    if (!assetId) {
      toast.error('Selecione um asset.');
      return;
    }
    if (!form.payment_date?.trim()) {
      toast.error('Data do pagamento é obrigatória.');
      return;
    }
    const paymentVal = parseDecimal(form.payment_value);
    if (paymentVal === null) {
      toast.error('Valor do pagamento é obrigatório e deve ser um número válido.');
      return;
    }
    const qtyVal = parseDecimal(form.quantity_at_time);
    if (form.quantity_at_time?.trim() !== '' && qtyVal === null) {
      toast.error('Quantidade inválida.');
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        asset: assetId,
        payment_type: form.payment_type,
        payment_date: form.payment_date,
        payment_value: paymentVal,
        quantity_at_time: qtyVal,
        notes: form.notes?.trim() || null,
      };
      await createAssetReturn(payload);
      toast.success('Return criado.');
      onSuccess?.();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Erro ao criar return.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white p-4 md:p-6 rounded-2xl shadow w-80 md:w-96 relative max-h-[90vh] overflow-y-auto">
        {submitting && (
          <div className="absolute inset-0 flex items-center justify-center z-10 bg-white/90 rounded-2xl">
            <div className="bg-white rounded-xl shadow-lg p-4 border border-gray-200">
              <SpinnerLoad />
            </div>
          </div>
        )}
        <div className="absolute top-2 right-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className={`text-gray-500 ${submitting ? 'opacity-50 cursor-not-allowed' : 'hover:text-gray-700'}`}
            title="Fechar"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <h2 className="text-lg font-semibold mb-4 pr-10">Novo return</h2>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div>
            <label className="block text-xs text-gray-500 mb-0.5">Asset</label>
            <select
              name="asset"
              value={form.asset}
              onChange={handleInputChange}
              className="w-full px-2 py-1.5 border rounded-md text-sm bg-white"
              disabled={submitting}
              required
            >
              <option value="">— Selecione —</option>
              {assets.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.asset_code ?? a.id} — {a.asset_name ?? '—'}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-0.5">Tipo de pagamento</label>
            <select
              name="payment_type"
              value={form.payment_type}
              onChange={handleInputChange}
              className="w-full px-2 py-1.5 border rounded-md text-sm bg-white"
              disabled={submitting}
            >
              {PAYMENT_TYPES.map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-0.5">Data do pagamento</label>
            <input
              type="date"
              name="payment_date"
              value={form.payment_date}
              onChange={handleInputChange}
              className="w-full px-2 py-1.5 border rounded-md text-sm"
              disabled={submitting}
              required
            />
          </div>

          <div className="flex gap-2">
            <div className="flex-1 min-w-0">
              <label className="block text-xs text-gray-500 mb-0.5">Valor (R$)</label>
              <input
                type="text"
                name="payment_value"
                value={form.payment_value}
                onChange={handleInputChange}
                placeholder="0,00"
                className="w-full px-2 py-1.5 border rounded-md text-sm tabular-nums"
                disabled={submitting}
                inputMode="decimal"
                required
              />
            </div>
            <div className="flex-1 min-w-0">
              <label className="block text-xs text-gray-500 mb-0.5">Quantidade na data (opcional)</label>
              <input
                type="text"
                name="quantity_at_time"
                value={form.quantity_at_time}
                onChange={handleInputChange}
                placeholder="Ex: 100"
                className="w-full px-2 py-1.5 border rounded-md text-sm tabular-nums"
                disabled={submitting}
                inputMode="decimal"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-0.5">Observações (opcional)</label>
            <textarea
              name="notes"
              value={form.notes}
              onChange={handleInputChange}
              placeholder="Notas"
              rows={2}
              className="w-full px-2 py-1.5 border rounded-md text-sm resize-none"
              disabled={submitting}
            />
          </div>

          <div className="flex gap-2 pt-2">
            <button
              type="submit"
              disabled={submitting}
              className={`flex-1 h-10 text-white rounded-2xl ${submitting ? 'bg-green-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}
            >
              {submitting ? 'Salvando...' : 'Salvar'}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 h-10 rounded-2xl border border-gray-300 text-gray-700 hover:bg-gray-100"
            >
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ModalReturnAdd;
